import React, { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { AzureDevOpsService, ProjectWorkItemData, WorkItemWithTimeLog } from '../services/AzureDevOpsService';
import { PublicClientApplication } from '@azure/msal-browser';
import { Spin, Table, Alert, Button, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { PRIORITY_DRIVERS_NONE } from '../Common/Constants';
import EditFieldsModal from './EditFieldsModal';

interface TimeAdministrationPageProps {
  useAdvancedMode: boolean;
  projectWorkItemData: ProjectWorkItemData[];
  usersData: any[];
  capexOptions: string[];
  govProjectOptions: string[];
  priorityDriverOptions: string[];
  initiativeOptions: string[];
  productOptions: string[];
  investmentClassOptions: string[];
}

const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh'
  }}>
    <Spin size="large" />
  </div>
);

const TimeAdministrationPage: React.FC<TimeAdministrationPageProps> = (props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [workItemsWithLogs, setWorkItemsWithLogs] = useState<WorkItemWithTimeLog[]>([]);
  const [filteredWorkItems, setFilteredWorkItems] = useState<WorkItemWithTimeLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedWorkItem, setSelectedWorkItem] = useState<WorkItemWithTimeLog | null>(null);
  const [selectedPriorityDriver, setSelectedPriorityDriver] = useState<string>('');
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const { accounts, instance } = useMsal();

  useEffect(() => {
    const fetchWorkItemsWithTimeLogs = async () => {
      if (accounts.length > 0) {
        try {
          setError(null);
          setIsLoading(true);
          setLoadingProgress('Fetching work items...');
          
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          
          const onProgressUpdate = (items: WorkItemWithTimeLog[]) => {
            setWorkItemsWithLogs(items);
            setFilteredWorkItems(items);
            setLoadingProgress(`Loaded ${items.length} work items...`);
            if (!isLoaded) {
              setIsLoaded(true); // Show table as soon as first batch loads
            }
          };
          
          const result = await devOpsService.fetchWorkItemsWithTimeLogs(organization, onProgressUpdate);
          setWorkItemsWithLogs(result);
          setFilteredWorkItems(result);
          setIsLoaded(true);
          setIsLoading(false);
          setLoadingProgress('');
        } catch (error) {
          console.error('Error fetching work items with time logs:', error);
          setError('Failed to fetch work items with time logs. Please try again later.');
          setIsLoaded(true);
          setIsLoading(false);
          setLoadingProgress('');
        }
      }
    };

    fetchWorkItemsWithTimeLogs();
  }, [accounts, instance]);

  const uniqueParentIds = useMemo(() => {
    const parentIds = new Set<string>();
    workItemsWithLogs.forEach(item => {
      const parentUrl = item.workItem.relations?.find((rel: any) => rel.rel === 'System.LinkTypes.Hierarchy-Reverse')?.url;
      if (parentUrl) {
        const parentId = parentUrl.split('/').pop();
        if (parentId) {
          parentIds.add(parentId);
        }
      }
    });
    return Array.from(parentIds).sort((a, b) => parseInt(a) - parseInt(b));
  }, [workItemsWithLogs]);

  useEffect(() => {
    let filtered = workItemsWithLogs;
    
    if (selectedPriorityDriver) {
      if (selectedPriorityDriver === PRIORITY_DRIVERS_NONE) {
        filtered = filtered.filter(item => {
          const field = item.inheritedFields?.priorityDriver;
          return !field || !field.value || field.value.trim() === '';
        });
      } else {
        filtered = filtered.filter(item => {
          const field = item.inheritedFields?.priorityDriver;
          return field && field.value && field.value === selectedPriorityDriver;
        });
      }
    }
    
    if (selectedParentId) {
      filtered = filtered.filter(item => {
        const parentUrl = item.workItem.relations?.find((rel: any) => rel.rel === 'System.LinkTypes.Hierarchy-Reverse')?.url;
        const parentId = parentUrl ? parentUrl.split('/').pop() : null;
        
        if (selectedParentId === 'NO_PARENT') {
          return !parentId;
        } else {
          return parentId === selectedParentId;
        }
      });
    }
    
    setFilteredWorkItems(filtered);
  }, [workItemsWithLogs, selectedPriorityDriver, selectedParentId]);

  const handleEditFields = (workItem: WorkItemWithTimeLog) => {
    setSelectedWorkItem(workItem);
    setEditModalVisible(true);
  };

  const handleModalClose = () => {
    setEditModalVisible(false);
    setSelectedWorkItem(null);
  };

  const handleFieldUpdate = (workItemId: number, updatedFields: any) => {
    setWorkItemsWithLogs(prevItems => 
      prevItems.map(item => {
        if (item.workItem.id === workItemId) {
          const updatedWorkItem = {
            ...item,
            workItem: {
              ...item.workItem,
              fields: {
                ...item.workItem.fields,
                ...updatedFields
              }
            }
          };
          
          if (updatedFields['Custom.GovProject']) {
            updatedWorkItem.inheritedFields = {
              ...updatedWorkItem.inheritedFields,
              govProject: { value: updatedFields['Custom.GovProject'], inherited: false }
            };
          }
          if (updatedFields['Custom.CapEx']) {
            updatedWorkItem.inheritedFields = {
              ...updatedWorkItem.inheritedFields,
              capex: { value: updatedFields['Custom.CapEx'], inherited: false }
            };
          }
          if (updatedFields['Custom.PriorityDriver']) {
            updatedWorkItem.inheritedFields = {
              ...updatedWorkItem.inheritedFields,
              priorityDriver: { value: updatedFields['Custom.PriorityDriver'], inherited: false }
            };
          }
          if (updatedFields['Custom.SensioProduct'] !== undefined) {
            updatedWorkItem.inheritedFields = {
              ...updatedWorkItem.inheritedFields,
              product: { value: updatedFields['Custom.SensioProduct'], inherited: false }
            };
          }
          if (updatedFields['Custom.Investmentclass'] !== undefined) {
            updatedWorkItem.inheritedFields = {
              ...updatedWorkItem.inheritedFields,
              investmentClass: { value: updatedFields['Custom.Investmentclass'], inherited: false }
            };
          }
          
          return updatedWorkItem;
        }
        return item;
      })
    );
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Work Item ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: any, record: WorkItemWithTimeLog) => (
        <a
          href={`https://dev.azure.com/sensio/_workitems/edit/${record.workItem.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {record.workItem.id}
        </a>
      ),
      width: 120,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: any, record: WorkItemWithTimeLog) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {record.workItem.fields['System.Title']}
        </div>
      ),
      width: 300,
    },
    {
      title: 'Project',
      dataIndex: 'project',
      key: 'project',
      render: (text: any, record: WorkItemWithTimeLog) => record.workItem.fields['System.TeamProject'],
      width: 150,
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (text: any, record: WorkItemWithTimeLog) => record.workItem.fields['System.State'],
      width: 100,
    },
    {
      title: (
        <div>
          <div style={{ marginBottom: '8px' }}>Parent</div>
          <Select
            placeholder="All"
            allowClear
            showSearch
            size="small"
            style={{ width: '100px' }}
            value={selectedParentId || undefined}
            onChange={(value) => setSelectedParentId(value || '')}
          >
            <Select.Option key="NO_PARENT" value="NO_PARENT">
              No Parent
            </Select.Option>
            {uniqueParentIds.map((parentId) => (
              <Select.Option key={parentId} value={parentId}>
                {parentId}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: 'parent',
      key: 'parent',
      render: (text: any, record: WorkItemWithTimeLog) => {
        const parentUrl = record.workItem.relations?.find((rel: any) => rel.rel === 'System.LinkTypes.Hierarchy-Reverse')?.url;
        if (!parentUrl) return '-';
        
        const parentId = parentUrl.split('/').pop();
        if (!parentId) return '-';
        
        return (
          <a
            href={`https://dev.azure.com/sensio/_workitems/edit/${parentId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {parentId}
          </a>
        );
      },
      sorter: (a: WorkItemWithTimeLog, b: WorkItemWithTimeLog) => {
        const getParentId = (item: WorkItemWithTimeLog) => {
          const parentUrl = item.workItem.relations?.find((rel: any) => rel.rel === 'System.LinkTypes.Hierarchy-Reverse')?.url;
          return parentUrl ? parentUrl.split('/').pop() || '' : '';
        };
        
        const parentIdA = getParentId(a);
        const parentIdB = getParentId(b);
        
        if (!parentIdA && !parentIdB) return 0;
        if (!parentIdA) return 1;
        if (!parentIdB) return -1;
        
        return parseInt(parentIdA) - parseInt(parentIdB);
      },
      width: 120,
    },
    {
      title: (
        <div>
          <div style={{ marginBottom: '8px' }}>Priority Driver</div>
          <Select
            placeholder="All"
            allowClear
            showSearch
            size="small"
            style={{ width: '140px' }}
            value={selectedPriorityDriver || undefined}
            onChange={(value) => setSelectedPriorityDriver(value || '')}
          >
            <Select.Option key={PRIORITY_DRIVERS_NONE} value={PRIORITY_DRIVERS_NONE}>
              Not Set
            </Select.Option>
            {props.priorityDriverOptions.map((option) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: 'priorityDriver',
      key: 'priorityDriver',
      render: (text: any, record: WorkItemWithTimeLog) => {
        const field = record.inheritedFields?.priorityDriver;
        if (!field || !field.value) return '-';
        return (
          <span style={{ color: field.inherited ? '#1890ff' : 'inherit' }}>
            {field.value}
            {field.inherited && ' (inherited)'}
          </span>
        );
      },
      width: 180,
    },
    {
      title: 'Initiative',
      dataIndex: 'initiative',
      key: 'initiative',
      render: (text: any, record: WorkItemWithTimeLog) => record.workItem.fields['Custom.Initiative'] || '-',
      width: 150,
    },
    {
      title: 'Gov Project',
      dataIndex: 'govProject',
      key: 'govProject',
      render: (text: any, record: WorkItemWithTimeLog) => {
        const field = record.inheritedFields?.govProject;
        if (!field || !field.value) return '-';
        return (
          <span style={{ color: field.inherited ? '#1890ff' : 'inherit' }}>
            {field.value}
            {field.inherited && ' (inherited)'}
          </span>
        );
      },
      width: 120,
    },
    {
      title: 'CapEx',
      dataIndex: 'capex',
      key: 'capex',
      render: (text: any, record: WorkItemWithTimeLog) => {
        const field = record.inheritedFields?.capex;
        if (!field || !field.value) return '-';
        return (
          <span style={{ color: field.inherited ? '#1890ff' : 'inherit' }}>
            {field.value}
            {field.inherited && ' (inherited)'}
          </span>
        );
      },
      width: 100,
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
      render: (text: any, record: WorkItemWithTimeLog) => {
        const field = record.inheritedFields?.product;
        if (!field || !field.value) return '-';
        return (
          <span style={{ color: field.inherited ? '#1890ff' : 'inherit' }}>
            {field.value}
            {field.inherited && ' (inherited)'}
          </span>
        );
      },
      width: 120,
    },
    {
      title: 'Investment Class',
      dataIndex: 'investmentClass',
      key: 'investmentClass',
      render: (text: any, record: WorkItemWithTimeLog) => {
        const field = record.inheritedFields?.investmentClass;
        if (!field || !field.value) return '-';
        return (
          <span style={{ color: field.inherited ? '#1890ff' : 'inherit' }}>
            {field.value}
            {field.inherited && ' (inherited)'}
          </span>
        );
      },
      width: 140,
    },
    {
      title: 'Total Time',
      dataIndex: 'totalDuration',
      key: 'totalDuration',
      render: (duration: number) => formatDuration(duration),
      sorter: (a: WorkItemWithTimeLog, b: WorkItemWithTimeLog) => a.totalDuration - b.totalDuration,
      width: 120,
    },
    {
      title: 'Last Logged',
      dataIndex: 'lastLogged',
      key: 'lastLogged',
      render: (text: any, record: WorkItemWithTimeLog) => {
        if (record.timeLogs.length === 0) return '-';
        const latest = record.timeLogs.reduce((latest, log) => 
          new Date(log.timestamp) > new Date(latest.timestamp) ? log : latest
        );
        return formatTimestamp(latest.timestamp);
      },
      sorter: (a: WorkItemWithTimeLog, b: WorkItemWithTimeLog) => {
        if (a.timeLogs.length === 0 && b.timeLogs.length === 0) return 0;
        if (a.timeLogs.length === 0) return 1;
        if (b.timeLogs.length === 0) return -1;
        
        const latestA = a.timeLogs.reduce((latest, log) => 
          new Date(log.timestamp) > new Date(latest.timestamp) ? log : latest
        );
        const latestB = b.timeLogs.reduce((latest, log) => 
          new Date(log.timestamp) > new Date(latest.timestamp) ? log : latest
        );
        
        return new Date(latestB.timestamp).getTime() - new Date(latestA.timestamp).getTime();
      },
      width: 150,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text: any, record: WorkItemWithTimeLog) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleEditFields(record)}
        >
          <EditOutlined />
          Edit Fields
        </Button>
      ),
      width: 120,
    },
  ];

  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h1>Time Administration</h1>
      <p>This page is only visible in Advanced Mode.</p>
      
      {error && (
        <div style={{ marginBottom: '20px' }}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
          />
        </div>
      )}
      
      <div style={{ marginTop: '20px' }}>
        <h2>Work Items with Time Logs (Last 200 Days)</h2>
        <p>Showing work items that have time tracking data in the last 200 days from Azure DevOps native fields.</p>
        
        {isLoading && loadingProgress && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d1ecf1' }}>
            <span style={{ marginRight: '8px' }}>
              <Spin size="small" />
            </span>
            {loadingProgress}
          </div>
        )}
        
        
        <Table
          dataSource={filteredWorkItems}
          columns={columns as any}
          rowKey={(record: WorkItemWithTimeLog) => record.workItem.id.toString()}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
        
        <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          <p>User: {accounts.length > 0 ? accounts[0].username : 'Not authenticated'}</p>
          <p>Advanced Mode: {props.useAdvancedMode ? 'Enabled' : 'Disabled'}</p>
          <p>Work Items with Time Logs: {workItemsWithLogs.length} (Filtered: {filteredWorkItems.length})</p>
          <p>Total Time Entries: {workItemsWithLogs.reduce((sum, item) => sum + item.timeLogs.length, 0)}</p>
        </div>
      </div>

      <EditFieldsModal
        visible={editModalVisible}
        workItem={selectedWorkItem}
        onClose={handleModalClose}
        onUpdate={handleFieldUpdate}
        capexOptions={props.capexOptions}
        govProjectOptions={props.govProjectOptions}
        priorityDriverOptions={props.priorityDriverOptions}
        initiativeOptions={props.initiativeOptions}
        productOptions={props.productOptions}
        investmentClassOptions={props.investmentClassOptions}
      />
    </div>
  );
};

export default TimeAdministrationPage;
